import { useContext } from 'react';
import { ConfigContext } from '@chr/navisphereonline-shared-components';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { RelatedBooking } from '@/common/apis/dtos';
import { GridColDef } from '@mui/x-data-grid-pro';
import { CustomGrid, createColumn, getColumnLink } from '@/common/components/grid/custom-grid';
import { GridColumn } from '@/common/components/grid/grid-settings';
import { getRollupBookingStatusLabelOrDefault } from '@/common/utilities/display-utilities';
import { useAnalytics } from '@/common/hooks/useAnalytics';

export interface ItemsSectionProps {
    items: RelatedBooking[];
}

export const RelatedBookingsItems = ({
    items
}: ItemsSectionProps) => {
    const config = useContext(ConfigContext) as AppConfig;
    const shipmentDetailsBaseUrl = `${config.baseUrl}/shipments/#/detail/`;
    const bookingDetailsBaseUrl = `${config.baseUrl}/booking/#/`;
    const translate = useTranslation();
    const analytics = useAnalytics();

    const createBookingColumn = (column: GridColumn, flex = 1,): GridColDef =>
    ({
        field: column.field, headerName: column.header,
        flex,
        renderCell: (cellValues) => {
            return (
                getColumnLink(cellValues.row.bookingNumber, 'bookingnumber-link', bookingDetailsBaseUrl, '', trackLink)
            );
        }
    });

    const createShipmentColumn = (column: GridColumn, flex = 1,): GridColDef =>
    ({
        field: column.field, headerName: column.header,
        flex,
        renderCell: (cellValues) => {
            return (
                getColumnLink(cellValues.row.shipmentNumber, 'shipment-link', shipmentDetailsBaseUrl, translate('Not planned on shipment'), trackLink)
            );
        }
    });

    function trackLink(name: string, label: string) {
        analytics.trackEvent(label, name, 'click');
    }

    const columns: GridColDef[] = [];
    columns.push(createBookingColumn({ header: translate('CHR Booking Number'), field: 'bookingNumber' }));
    columns.push(createColumn({ header: translate('Status'), field: 'status' }));
    columns.push(createShipmentColumn({ header: translate('Shipment'), field: 'shipmentNumber' }));

    const processItems = items.map(item => {
        return {
            bookingNumber: item.bookingNumber,
            status: getRollupBookingStatusLabelOrDefault(translate, item.status),
            shipmentNumber: item.shipmentNumber
        };
    });

    return (
        <CustomGrid
            dataTestId="booking-items"
            columns={columns}
            items={processItems}
            sortModel={[
                {
                    field: 'bookingNumber',
                    sort: 'desc',
                },
            ]}
        />
    );
};
